<template>
  <div
    :class="rootClasses"
    :style="progressStyle"
    role="progressbar"
    :aria-valuemin="0"
    :aria-valuemax="normalizedMax"
    :aria-valuenow="normalizedValue"
    data-cy="ds-progress-bar"
  >
    <div
      :class="DS_PROGRESS_BAR_CLASSES.Track"
      data-cy="ds-progress-bar__track"
      aria-hidden="true"
    >
      <div
        :class="DS_PROGRESS_BAR_CLASSES.Indicator"
        data-cy="ds-progress-bar__indicator"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { DSProgressBarProps } from "@/src/design_system/feedback/progress_bar/types/props";
import { DS_PROGRESS_BAR_VARIANT } from "@/src/design_system/feedback/progress_bar/types/variant";
import {
  DS_PROGRESS_BAR_CLASSES,
  DS_PROGRESS_BAR_VARIANT_TO_CLASS_MAP,
} from "@/src/design_system/feedback/progress_bar/constants/classes";

const props = withDefaults(defineProps<DSProgressBarProps>(), {
  variant: DS_PROGRESS_BAR_VARIANT.Primary,
  revert: false,
  max: 100,
});

const normalizedMax = computed(() => {
  const max = Number(props.max);
  if (!Number.isFinite(max)) return 100;

  return Math.max(0, max);
});

const normalizedValue = computed(() => {
  const value = Number(props.value);
  if (!Number.isFinite(value)) return 0;

  return Math.min(normalizedMax.value, Math.max(0, value));
});

const progressPercent = computed(() => {
  if (normalizedMax.value === 0) return 0;

  return (normalizedValue.value / normalizedMax.value) * 100;
});

const progressStyle = computed(() => {
  return {
    "--ds-progress-bar-value": `${progressPercent.value}%`,
  };
});

const variantClass = computed(() => {
  return (
    DS_PROGRESS_BAR_VARIANT_TO_CLASS_MAP[props.variant] ??
    DS_PROGRESS_BAR_VARIANT_TO_CLASS_MAP[DS_PROGRESS_BAR_VARIANT.Primary]
  );
});

const rootClasses = computed(() => [
  DS_PROGRESS_BAR_CLASSES.Root,
  variantClass.value,
  {
    [DS_PROGRESS_BAR_CLASSES.Revert]: props.revert,
  },
]);
</script>
